import { useMemo, useState } from "react";
import * as Animate from "react-reveal";
import { ArrowUpRight } from "../../../components/svgs";

const RoleCard = ({ number, job }) => (
  <article className="h-full flex flex-col bg-white rounded-2xl p-7">
    <div className="flex items-start justify-between mb-6">
      <span className="w-9 h-9 rounded-lg bg-hire-tint-blue text-primary grid place-items-center font-poppins text-sm font-extrabold">
        {number}
      </span>
      {job?.jobType && (
        <span className="text-[#8e5e00] font-poppins text-xs font-extrabold tracking-widest uppercase text-right">
          {job.jobType}
        </span>
      )}
    </div>
    <h3 className="font-poppins font-extrabold text-xl text-primary mb-4 flex-1">
      {job?.title}
    </h3>
    <div className="flex flex-wrap gap-2 mb-5">
      {job?.jobWorkMode && (
        <span className="rounded-full border border-hire-line px-3.5 py-1.5 font-poppins text-xs font-bold text-default">
          {job.jobWorkMode}
        </span>
      )}
      {job?.jobType && (
        <span className="rounded-full border border-hire-line px-3.5 py-1.5 font-poppins text-xs font-bold text-default">
          {job.jobType}
        </span>
      )}
    </div>
    <a
      href={job?.jobApplyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="pt-5 border-t border-hire-line font-poppins font-extrabold text-primary text-sm flex items-center gap-2 hover:text-secondary transition-colors"
    >
      View role &amp; apply
      <ArrowUpRight className="w-3.5 h-3.5" />
    </a>
  </article>
);

const CareerOpenRolesSection = ({ loading, jobs }) => {
  const [query, setQuery] = useState("");

  const filteredJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs || [];
    return (jobs || []).filter((job) =>
      [job?.title, job?.jobType, job?.jobWorkMode]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q))
    );
  }, [jobs, query]);

  return (
    <section className="bg-primary py-16 md:py-20 lg:py-24 px-4" id="roles">
      <div className="container mx-auto">
        <Animate.Fade bottom>
          <p className="flex items-center font-poppins gap-3 text-secondary text-sm font-extrabold tracking-widest uppercase mb-6">
            <span className="w-8 h-1 rounded-full bg-secondary inline-block"></span>
            Open opportunities
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start mb-10">
            <h2 className="lg:col-span-2 text-3xl md:text-4xl lg:text-5xl font-poppins leading-tight font-black max-w-xl tracking-[-0.03em] text-white">
              Find the work you want to do next.
            </h2>
            <p className="text-indigo-100 text-base font-poppins leading-relaxed lg:pt-2">
              Start with the role. The longer story can come after.
            </p>
          </div>
        </Animate.Fade>

        <Animate.Fade bottom>
          <div
            className="rounded-2xl p-3 flex items-center gap-3 mb-8"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search roles, teams or location"
              className="flex-1 bg-white rounded-xl px-4 py-3.5 font-poppins text-sm text-default placeholder-gray focus:outline-none"
            />
          </div>
        </Animate.Fade>

        <p className="font-poppins text-white text-sm mb-6">
          <strong className="text-secondary font-extrabold">
            {filteredJobs?.length || 0}
          </strong>{" "}
          {filteredJobs?.length === 1 ? "opportunity" : "opportunities"}
        </p>

        {loading ? (
          <p className="text-center font-poppins text-white py-10">
            Loading roles...
          </p>
        ) : filteredJobs?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredJobs.map((job, index) => (
              <Animate.Fade bottom key={job._id}>
                <RoleCard
                  number={String(index + 1).padStart(2, "0")}
                  job={job}
                />
              </Animate.Fade>
            ))}
          </div>
        ) : (
          <p className="text-center font-poppins text-indigo-100 py-10">
            No open roles match your search right now. Check back later.
          </p>
        )}

        <p className="font-poppins text-indigo-100 text-xs mt-8">
          Roles shown are currently listed by Utiva. Each application opens in
          the official Utiva form.
        </p>
      </div>
    </section>
  );
};

export default CareerOpenRolesSection;
